import { useEffect, useRef } from "react";
import { nodeKindsForQuickAdd, type CanvasMenuState } from "../context-menu.js";
import type { CanvasNodeKind } from "../nodes/node-types.js";
import { NodeQuickAddMenu } from "./NodeQuickAddMenu.js";

export interface CanvasContextMenuProps {
  state: CanvasMenuState;
  onClose(): void;
  onCreate(kind: CanvasNodeKind, state: CanvasMenuState): void;
  onEdit(nodeId: string): void;
  onCopy(nodeId: string): void;
  onBranch(nodeId: string): void;
  onToggleFold(nodeId: string): void;
  onDelete(nodeId: string): void;
  onDeleteEdge(edgeId: string): void;
  isFolded(nodeId: string): boolean;
}

export function CanvasContextMenu(props: CanvasContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const firstActionRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(
    typeof document === "undefined" || !(document.activeElement instanceof HTMLElement)
      ? null
      : document.activeElement
  );
  const nodeState = props.state.kind === "node" ? props.state : undefined;
  const edgeState = props.state.kind === "edge" ? props.state : undefined;

  useEffect(() => {
    if (nodeState || edgeState) firstActionRef.current?.focus();
  }, [edgeState, nodeState]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") props.onClose();
    };
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) props.onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    window.addEventListener("pointerdown", closeOnOutsidePointer, true);
    return () => {
      window.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("pointerdown", closeOnOutsidePointer, true);
    };
  }, [props]);

  useEffect(() => () => restoreFocusRef.current?.focus(), []);

  const style = {
    left: Math.max(8, props.state.client.x),
    top: Math.max(8, props.state.client.y)
  };
  return (
    <div
      ref={menuRef}
      className="studio-context-menu"
      role="menu"
      aria-label={props.state.kind === "blank" ? "画布快速创建菜单" : props.state.kind === "node" ? "节点操作菜单" : "连线操作菜单"}
      style={style}
      onContextMenu={(event) => event.preventDefault()}
    >
      {props.state.kind === "blank" ? (
        <NodeQuickAddMenu
          kinds={nodeKindsForQuickAdd()}
          onCreate={(kind) => props.onCreate(kind, props.state)}
        />
      ) : nodeState ? (
        <div className="studio-context-menu__node-actions" role="group" aria-label="节点操作">
          <button role="menuitem" ref={firstActionRef} type="button" aria-label="编辑节点" onClick={() => props.onEdit(nodeState.nodeId)}>编辑</button>
          <button role="menuitem" type="button" aria-label="复制节点" onClick={() => props.onCopy(nodeState.nodeId)}>复制</button>
          <button role="menuitem" type="button" aria-label="复制为分支" onClick={() => props.onBranch(nodeState.nodeId)}>复制为分支</button>
          <button role="menuitem" type="button" aria-label={props.isFolded(nodeState.nodeId) ? "展开节点" : "折叠节点"} onClick={() => props.onToggleFold(nodeState.nodeId)}>
            {props.isFolded(nodeState.nodeId) ? "展开" : "折叠"}
          </button>
          <button role="menuitem" type="button" className="studio-context-menu__delete" aria-label="删除节点" onClick={() => props.onDelete(nodeState.nodeId)}>删除</button>
        </div>
      ) : edgeState ? (
        <div className="studio-context-menu__edge-actions" role="group" aria-label="连线操作">
          <button
            role="menuitem"
            ref={firstActionRef}
            type="button"
            className="studio-context-menu__delete"
            aria-label="删除连线"
            onClick={() => props.onDeleteEdge(edgeState.edgeId)}
          >
            删除连线
          </button>
        </div>
      ) : null}
    </div>
  );
}
